import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useTheme } from "../lib/theme";
import { ArrowUpRight, MapPin, Briefcase, Users, Broadcast, Archive } from "@phosphor-icons/react";
import ApplyModal from "../components/ApplyModal";

export default function Careers() {
  const { settings } = useTheme();
  const [positions, setPositions] = useState([]);
  const [pastRoles, setPastRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [hrDepts, setHrDepts] = useState({ source: "loading", departments: [] });
  const [dept, setDept] = useState("All");
  const [applyingTo, setApplyingTo] = useState(null);

  useEffect(() => {
    api.get("/positions").then(({ data }) => setPositions(data)).catch(() => {});
    api.get("/positions/departments").then(({ data }) => setDepartments(data.departments || [])).catch(() => {});
    api.get("/positions/hr-departments").then(({ data }) => setHrDepts(data)).catch(() => setHrDepts({ source: "error", departments: [] }));
    api.get("/positions", { params: { archived_only: true } }).then(({ data }) => setPastRoles(data)).catch(() => {});
  }, []);

  const showPast = settings?.show_past_roles !== false && pastRoles.length > 0;

  const filtered = useMemo(
    () => (dept === "All" ? positions : positions.filter((p) => p.department === dept)),
    [positions, dept]
  );

  // Show union of local + HRMS departments so filters reflect the live org
  const allDepartments = useMemo(() => {
    const set = new Set([...(departments || []), ...((hrDepts && hrDepts.departments) || [])]);
    return Array.from(set).sort();
  }, [departments, hrDepts]);

  return (
    <div data-testid="careers-page" className="px-6 md:px-14 py-16 md:py-24">
      <div className="max-w-6xl">
        <div className="text-overline mb-4">◢ Careers · we're hiring</div>
        <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1]">
          Build the systems<br />
          that <span className="text-[#FF4500]">run</span> the floor.
        </h1>
        <p className="mt-5 text-[#8E8E93] max-w-2xl text-[15px] leading-relaxed">
          Wisewit ships AI systems into real plants. We hire engineers who like talking to operators
          and shipping things that survive Monday morning. All applications flow through our ATS at
          <a href="https://hr.wisewit.ai" target="_blank" rel="noreferrer" className="text-[#DFFF00] mx-1.5 underline-offset-4 hover:underline">hr.wisewit.ai</a>
          — your profile is tracked from application through onboarding.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 items-center" data-testid="careers-filters">
        <Chip label="All" active={dept === "All"} onClick={() => setDept("All")} />
        {allDepartments.map((d) => (
          <Chip key={d} label={d} active={dept === d} onClick={() => setDept(d)} testid={`careers-filter-${d.toLowerCase().replace(/\s+/g, "-")}`} />
        ))}
        {["hrms", "cache"].includes(hrDepts.source) && (
          <span
            className="ml-2 inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.22em] uppercase text-[#DFFF00] border border-[#DFFF00]/40 px-2 py-1.5"
            title="Departments synced live from hr.wisewit.ai"
            data-testid="careers-live-badge"
          >
            <Broadcast size={10} weight="fill" /> Live · {hrDepts.departments.length} depts · HRMS
          </span>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-16 ww-card p-10 max-w-3xl">
          <Users size={24} color="#DFFF00" weight="duotone" />
          <div className="mt-4 font-display text-2xl text-white">No open roles in {dept} right now.</div>
          <p className="mt-2 text-[#8E8E93] text-sm">Send us your profile anyway — we track strong candidates and reach out when there's a fit.</p>
          <a href="https://hr.wisewit.ai" target="_blank" rel="noreferrer" className="btn-secondary mt-6 inline-flex">Open the ATS →</a>
        </div>
      ) : (
        <div className="mt-10 max-w-6xl border border-[#2A2A2A]" data-testid="careers-list">
          {filtered.map((p) => (
            <article
              key={p.id}
              data-testid={`position-${p.id}`}
              className="border-t border-[#2A2A2A] first:border-t-0 p-6 md:p-8 hover:bg-[#0a0a0a] transition-colors group"
            >
              <div className="grid md:grid-cols-12 gap-4 items-start">
                <div className="md:col-span-7">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">{p.department}</span>
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">{p.seniority}</span>
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">{p.employment_type}</span>
                  </div>
                  <h3 className="mt-2 font-display text-2xl md:text-3xl text-white">{p.title}</h3>
                  <p className="mt-3 text-[#D7D7D9] text-sm leading-relaxed max-w-xl">{p.summary}</p>
                  <div className="mt-4 flex items-center gap-4 text-[#8E8E93] text-xs">
                    <span className="flex items-center gap-1.5"><MapPin size={14} weight="duotone" /> {p.location}</span>
                    <span className="flex items-center gap-1.5"><Briefcase size={14} weight="duotone" /> ATS: {p.ats_external_id || "—"}</span>
                  </div>
                </div>
                <div className="md:col-span-5 flex md:justify-end gap-3">
                  <Link to={`/careers/${p.id}`} data-testid={`position-${p.id}-details`} className="btn-secondary">
                    Details
                  </Link>
                  <button
                    type="button"
                    onClick={() => setApplyingTo(p)}
                    data-testid={`position-${p.id}-apply`}
                    className="btn-primary"
                  >
                    Apply <ArrowUpRight size={14} weight="bold" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="mt-16 max-w-6xl ww-card p-8">
        <div className="text-overline mb-2">◢ How applications work</div>
        <h3 className="font-display text-2xl text-white">One quick form. No login required.</h3>
        <p className="mt-3 text-[#8E8E93] text-sm max-w-2xl">
          Click <strong className="text-white">Apply</strong> and you'll fill a short form right here — resume, cover message,
          and a couple of role-specific questions. We'll email you a confirmation and our recruiting team reviews every
          submission within 5 business days.
        </p>
      </div>

      <ApplyModal open={!!applyingTo} position={applyingTo} onClose={() => setApplyingTo(null)} />

      {showPast && (
        <div className="mt-16 max-w-6xl" data-testid="past-roles-section">
          <div className="flex items-center gap-2 mb-4">
            <Archive size={16} color="#8E8E93" weight="duotone" />
            <div className="text-overline">◢ Past roles · previously hired for</div>
          </div>
          <div className="border border-[#2A2A2A]">
            {pastRoles.map((p) => (
              <div key={p.id} data-testid={`past-role-${p.id}`} className="grid grid-cols-12 gap-3 px-6 py-4 border-t border-[#2A2A2A] first:border-t-0 items-center">
                <div className="col-span-12 md:col-span-6 text-[#D7D7D9]">{p.title}</div>
                <div className="col-span-6 md:col-span-3 font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">{p.department}</div>
                <div className="col-span-6 md:col-span-3 font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93] md:text-right">{p.location}</div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[#555] text-xs">These roles are filled and closed to new applications.</p>
        </div>
      )}
    </div>
  );
}

function Chip({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors ${
        active ? "border-[#FF4500] text-white bg-[#FF4500]/10" : "border-[#2A2A2A] text-[#8E8E93] hover:text-white hover:border-[#404040]"
      }`}
    >
      {label}
    </button>
  );
}
